/**
 * TipKit-style "don't ask again" memory for guides: which ones a user has
 * finished, and whether they've ever opened the launcher at all. Keyed per
 * user so a shared machine doesn't leak one person's progress into another's
 * session. Storage is best-effort — corrupt JSON, a missing key, or
 * localStorage throwing outright (private browsing) all degrade to "nothing
 * completed yet" rather than breaking the page.
 */ import { useCallback, useSyncExternalStore } from 'react';
import { useAuth } from '../contexts/authContext';
const EMPTY = {
    completed: [],
    launcherOpened: false
};
function key(userId) {
    return `guide-memory:${userId || 'anon'}`;
}
function isGuideMemory(value) {
    const v = value;
    return !!v && typeof v === 'object' && Array.isArray(v.completed) && v.completed.every((c)=>typeof c === 'string') && typeof v.launcherOpened === 'boolean';
}
export function readGuideMemory(userId) {
    try {
        const raw = localStorage.getItem(key(userId));
        if (!raw) return EMPTY;
        const parsed = JSON.parse(raw);
        return isGuideMemory(parsed) ? parsed : EMPTY;
    } catch  {
        return EMPTY;
    }
}
// Per-user cache so useSyncExternalStore's snapshot is referentially stable
// between renders — it must not change unless a write actually happened.
const cache = new Map();
const subscribers = new Set();
function write(userId, memory) {
    try {
        localStorage.setItem(key(userId), JSON.stringify(memory));
    } catch  {
    // Private mode or a full quota: the memory just doesn't persist.
    }
    cache.delete(key(userId));
    for (const fn of subscribers)fn();
    return memory;
}
export function markGuideCompleted(userId, name) {
    const current = readGuideMemory(userId);
    if (current.completed.includes(name)) return write(userId, current);
    return write(userId, {
        ...current,
        completed: [
            ...current.completed,
            name
        ]
    });
}
export function markLauncherOpened(userId) {
    const current = readGuideMemory(userId);
    return write(userId, {
        ...current,
        launcherOpened: true
    });
}
function snapshot(userId) {
    const k = key(userId);
    let memory = cache.get(k);
    if (!memory) {
        memory = readGuideMemory(userId);
        cache.set(k, memory);
    }
    return memory;
}
function subscribe(onChange) {
    subscribers.add(onChange);
    return ()=>subscribers.delete(onChange);
}
/** Keeps the launcher and the help page's catalog in sync within the tab. */ export function useGuideMemory() {
    const { user } = useAuth();
    const userId = user?.id ?? null;
    const memory = useSyncExternalStore(subscribe, ()=>snapshot(userId));
    const completeGuide = useCallback((name)=>markGuideCompleted(userId, name), [
        userId
    ]);
    const openLauncher = useCallback(()=>markLauncherOpened(userId), [
        userId
    ]);
    return {
        memory,
        completeGuide,
        openLauncher
    };
}
